import { packsClient } from "lib/clients";
import useSWR from "swr";
import { useLoggedinUser } from ".";
import { decrypt, useE2EEContext } from "lib/e2ee";
import { sortAndFormat } from "domains/pack/utils";
import { needOldPassword } from "lib/utils";

export enum ItemLevels {
  ASSET = 'asset',
  GROUP = 'group',
  ROW = 'row',
}

export type Asset = {
  id: string;
  created: string;
  group_id?: string;
  row_id?: string;
  data: any;
  indent: number;
  order: number;
  pack_id: string;
  type: 'link' | 'text' | 'embed' | 'file';
  updated: string;
  user_id: string;
  level: ItemLevels.ASSET;
  enc_data: string;
};

export type AssetGroup = {
  id: string;
  level: ItemLevels.GROUP,
  children: Asset[] | AssetRow[];
}

export type AssetRow = {
  id: string;
  level: ItemLevels.ROW,
  children: Asset[];
}

const fetcher = async (_key, id, sessionKey) => {
  const { data } = await packsClient.getPackAssets(id);

  const assets = [];

  for (const asset of data) {
    if (asset.enc_data) {
      const data = await decrypt(asset.enc_data, sessionKey);
      assets.push({ ...asset, data });
    }
    else {
      assets.push(asset);
    }
  }

  return sortAndFormat(assets);
}

type UsePackAssets = (packId: string) => any;

export const usePackAssets: UsePackAssets = (pack: any) => {
  const { data: user } = useLoggedinUser();
  const { needNonce } = useE2EEContext();
  const reallyNeedNonce = pack?.key && needNonce;
  const { data, error, isValidating, mutate } = useSWR(pack?.id && !needOldPassword(pack, user) && !reallyNeedNonce ? [`assets/${pack.id}`, pack.id, pack.key] : null, fetcher, { revalidateIfStale: false, revalidateOnFocus: false, revalidateOnReconnect: false }); // syncPack will keep assets up-to-date without impacting bandwidth limits.

  return {
    data,
    getAssetById: (id) => data.find(asset => asset.id === id),
    isValidating,
    isLoading: !data && !error,
    error,
    mutate,
  };
};
